import { SafeHTMLContent } from "lib/types";
import { application } from "controllers/index";
import { ConfirmTemplateController } from "decor";
import {
  DecorSuiteModalsModalIdentifier,
  DecorSuiteModalsConfirmTemplateIdentifier,
} from "decor";

// ── Event name constants ──────────────────────────────────────────────────────
//
// Stimulus scoped-event names dispatched by Decor::Suite::Modals::Modal —
// shape `<identifier>:<event>`. Built from the canonical identifier export
// so a rename in Decor flows through to every Confinus caller via a single
// import.
//
// `enum` is replaced by `as const` because TS enums require string-literal
// member values (no template interpolation allowed); the consumer API
// (`ModalEvents.Open`) is unchanged.

export const ModalEvents = {
  Open: `${DecorSuiteModalsModalIdentifier}:open`,
  Opened: `${DecorSuiteModalsModalIdentifier}:opened`,
  Close: `${DecorSuiteModalsModalIdentifier}:close`,
  Closed: `${DecorSuiteModalsModalIdentifier}:closed`,
  // Legacy aliases kept for backward-compat with external listeners
  Opening: `${DecorSuiteModalsModalIdentifier}:opening`,
  Loading: `${DecorSuiteModalsModalIdentifier}:loading`,
  Loaded: `${DecorSuiteModalsModalIdentifier}:loaded`,
  Ready: `${DecorSuiteModalsModalIdentifier}:ready`,
  Closing: `${DecorSuiteModalsModalIdentifier}:closing`,
} as const;
export type ModalEvents = (typeof ModalEvents)[keyof typeof ModalEvents];

// ── Event detail types ────────────────────────────────────────────────────────

export interface ModalOpenEventDetail {
  id?: string;
  // snake_case (new convention from Phlex / spec §4.6)
  content_href?: string;
  // camelCase kept for backward-compat with existing ModalOpenButton controller
  contentHref?: string;
  // Pre-rendered placeholder body HTML, injected before the fetch in shared-modal pattern
  initial_content?: SafeHTMLContent;
  // Per-open title override — used by shared-modal pattern so the dialog header
  // reflects row-specific context (e.g. SKU).
  title?: string;
}

export interface ModalCloseEventDetail {
  id?: string;
  close_reason?: string;
  closeReason?: string;
  action?: string;
}

/** Unified lifecycle event emitted on the dialog element (bubbles: true) */
export interface ModalLifecycleEventDetail {
  reason?: string;
  shownWith?: Record<string, unknown>;
  closeReason?: string;
  action?: string;
}

export type ModalLifecycleEvent = CustomEvent<ModalLifecycleEventDetail>;
export type ModalOpenEvent = CustomEvent<ModalOpenEventDetail>;
export type ModalCloseEvent = CustomEvent<ModalCloseEventDetail>;

/** Options for the `showModal` helper. */
export type ModalShowOptions = ModalOpenEventDetail & {
  onClosing?: (evt: ModalLifecycleEvent) => void;
  initial_content?: SafeHTMLContent;
};

// ── Confirm dialog types ──────────────────────────────────────────────────────

export interface ModalConfirmOptions {
  title?: string;
  message?: string;
  /** Pre-rendered HTML body content — caller must pass a SafeHTMLContent object. */
  messageHTML?: SafeHTMLContent;
  type?: "info" | "warning" | "error" | "success" | "destructive";
  defaultReason?: string;
  negativeButtonReason?: string;
  negativeButtonLabel?: string;
  positiveButtonReason?: string;
  positiveButtonLabel?: string;
  onClosing?: (event: ModalLifecycleEvent) => void;
}

// The overlays partial renders Decor::Suite::Modals::ConfirmTemplate, which
// Vident registers under the Suite identifier. spawnConfirmDialog clones that
// template element into <body>, so the controller we look up after insertion
// must match the Suite identifier — otherwise getControllerForElementAndIdentifier
// returns null and the modal never gets its fill/wire/showModal calls.
const CONFIRM_TEMPLATE_IDENTIFIER = DecorSuiteModalsConfirmTemplateIdentifier;

// ── spawnConfirmDialog ────────────────────────────────────────────────────────

function spawnConfirmDialog(options: ModalConfirmOptions): void {
  // Re-entrancy guard: if a confirm dialog clone already lives outside the
  // <template> (it has been spawned and is either still showing or queued to
  // show on the next frame), don't stack another. The template stays inside
  // <template> so its dialog isn't a direct child of <body>.
  if (document.body.querySelector(`:scope > dialog.decor-modal--confirm`)) {
    return;
  }

  const tpl = document.getElementById(
    "cf-confirm-modal-template",
  ) as HTMLTemplateElement | null;
  if (!tpl) {
    console.warn(
      "[confinus-ui] cf-confirm-modal-template not found in the DOM. " +
        "The page is missing _overlays.html.erb or it was not rendered. " +
        "spawnConfirmDialog cannot spawn a dialog.",
    );
    return;
  }

  const fragment = tpl.content.cloneNode(true) as DocumentFragment;
  const dialog = fragment.querySelector("dialog") as HTMLDialogElement | null;
  if (!dialog) {
    console.warn(
      "[confinus-ui] cf-confirm-modal-template did not contain a <dialog> element.",
    );
    return;
  }

  document.body.appendChild(dialog);

  // Wire the caller's onClosing callback. The confirm-template controller
  // only dispatches a `decor--suite--modals--modal:closing` event on the
  // dialog itself — it doesn't invoke options.onClosing — so without this
  // bridge a caller's "user accepted" handler never runs.
  if (options.onClosing) {
    const onClosing = options.onClosing;
    dialog.addEventListener(
      "decor--suite--modals--modal:closing",
      ((event: Event) => {
        onClosing(event as ModalLifecycleEvent);
      }) as EventListener,
      { once: true },
    );
  }

  // Wait one tick for Stimulus to connect the controller after DOM insertion.
  requestAnimationFrame(() => {
    const ctl = application.getControllerForElementAndIdentifier(
      dialog,
      CONFIRM_TEMPLATE_IDENTIFIER,
    ) as ConfirmTemplateController | null;

    if (!ctl) {
      console.warn(
        `[confinus-ui] ${CONFIRM_TEMPLATE_IDENTIFIER} controller did not connect ` +
          "after requestAnimationFrame. Dialog may be unstyled.",
      );
      return;
    }

    ctl.fillSlots(options);
    ctl.applyVariant(options.type ?? "info");
    ctl.wireCloseHandlers(options);
    dialog.showModal();
  });
}

// ── showConfirmModal (canonical entry point) ──────────────────────────────────

export function showConfirmModal(opts: ModalConfirmOptions): void {
  spawnConfirmDialog(opts);
}

// Open a Modal by dispatching `decor--suite--modals--modal:open`.
//
// Accepted options (see ModalShowOptions):
//   id          — target a specific modal element by its HTML id (required for
//                 normal use; omitting broadcasts to every Modal on the page).
//   content_href / contentHref — URL to lazy-load into the modal body on first
//                 open. Both forms are accepted; the snake_case form is canonical.
//   onClosing   — Callback invoked once when the modal closes, with the
//                 `:closed` lifecycle detail (`{ reason, closeReason }`). The
//                 Decor Suite Modal only emits `:closed` (the legacy `:closing`
//                 alias is never dispatched by it), so we must bind to `:closed`
//                 here or the callback never fires.
export function showModal(opts: ModalShowOptions) {
  const {
    onClosing,
    contentHref,
    content_href,
    initial_content,
    title,
    ...rest
  } = opts;

  function closingHandler(e: Event) {
    window.removeEventListener(ModalEvents.Closed, closingHandler);
    if (onClosing) {
      onClosing(e as ModalLifecycleEvent);
    }
  }

  window.addEventListener(ModalEvents.Closed, closingHandler);

  // Normalise to snake_case (new convention per spec §4.6) while preserving
  // camelCase for controllers that still read `contentHref`.
  const resolvedHref = content_href ?? contentHref;
  const detail: ModalShowOptions = {
    ...rest,
    ...(resolvedHref
      ? { content_href: resolvedHref, contentHref: resolvedHref }
      : {}),
    ...(initial_content ? { initial_content } : {}),
    ...(title ? { title } : {}),
  };

  const evt = new CustomEvent(ModalEvents.Open, {
    bubbles: true,
    cancelable: false,
    detail,
  });
  window.dispatchEvent(evt);
}

// Close a Modal by dispatching `decor--suite--modals--modal:close`.
//
// Accepted options:
//   id           — target a specific modal element; omit to close all.
//   close_reason / closeReason — string passed as the dialog returnValue and
//                   surfaced in the `decor--suite--modals--modal:closed` lifecycle event.
export function closeModal(opts?: ModalCloseEventDetail) {
  const evt = new CustomEvent(ModalEvents.Close, {
    bubbles: true,
    cancelable: false,
    detail: opts,
  });
  window.dispatchEvent(evt);
}
